import { useLayoutEffect, useRef, useState } from 'react'

/**
 * The gap a filled pane leaves below itself. Matches the Layout `main` L/R gutter
 * (`p:6` on the 4px scale = 24px), which is the whole point: a full-page pane should
 * end the same distance from the bottom edge as it does from the sides
 * (working-conventions §22).
 */
export const FILL_BOTTOM_MARGIN = 24

export interface UseFillBottomOptions {
  /** Off = the hook returns `null` and does no work. */
  enabled?: boolean
  /**
   * Selector for the descendant whose TOP defines where the fill starts, searched
   * inside the ref'd box. Use it when the box also contains chrome above the content
   * (the shared `Table` passes `.MuiDataGrid-root`, because the box wraps the
   * search/filter row too and measuring the box top would overflow by its height).
   * Omitted = measure the box itself.
   */
  measureSelector?: string
  /** Floor, so a short viewport degrades to a scrollable pane rather than a sliver. */
  minHeight?: number
  /** Values that should force a re-measure (row counts, loading flags). */
  deps?: readonly unknown[]
}

/**
 * Size an element so its bottom sits exactly {@link FILL_BOTTOM_MARGIN} above the
 * viewport bottom, by measuring its own top on every layout that could have moved it.
 *
 * ## Why this is a hook and not arithmetic you copy
 *
 * §22 exists because per-page `topOffset` magic numbers left inconsistent 50px+ dead
 * zones. The fix was to MEASURE instead of guess — but the measurement then got
 * hand-rolled a second time, in the certificate Scans tab, as ten lines that listened
 * to `window.resize` and one data-length dep and nothing else. That version is not a
 * smaller version of this one; it is a version that goes stale, because almost nothing
 * that moves a pane's top is a window resize:
 *
 * - A skeleton→content swap, a collapsing banner, or a sticky header settling reflows
 *   the top with the viewport unchanged, so no `resize` fires. Stale height, dead zone.
 * - MUI `Collapse` (the bulk-actions bar) animates height over 300ms, so a measure at
 *   t=0 reads a layout that no longer exists 300ms later. Visible jump.
 * - CSS `zoom` — which the CCD preview pane sets on `body` — makes
 *   `getBoundingClientRect()` report VISUAL px while the `height` we set is interpreted
 *   in LAYOUT px. Mixing them under-fills by exactly the zoom factor, and NONE of
 *   `resize` / `visualViewport` / a `<html>` ResizeObserver fires on a zoom change.
 *
 * So the arithmetic (`innerHeight - top - 24`) is the trivial part and the triggering is
 * the whole job. Extracted from `components/display/Table/Table.tsx`, which had all of
 * the above hard-won and was the only place that did (§28/§36 — the second hand-roll is
 * the signal to extract, and this one had already drifted before it was noticed).
 *
 * Returns a ref to attach to the box and the height to apply, or `null` before the
 * first measure — render a placeholder at that point rather than letting the pane mount
 * at a wrong size and remount (which reads as a flash).
 */
export function useFillBottom<T extends HTMLElement = HTMLDivElement>(
  options: UseFillBottomOptions = {},
): { ref: React.MutableRefObject<T | null>; height: number | null } {
  const { enabled = true, measureSelector, minHeight = 200, deps = [] } = options

  const ref = useRef<T | null>(null)
  const [height, setHeight] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (!enabled) return
    const recompute = () => {
      const el = ref.current
      if (!el) return
      const target = measureSelector
        ? ((el.querySelector(measureSelector) as HTMLElement | null) ?? el)
        : el
      const top = target.getBoundingClientRect().top
      // Recover the cumulative CSS zoom from the box itself (visual rect height ÷
      // layout offsetHeight) and convert the desired VISUAL height into LAYOUT px, so
      // the rendered fill is exact at any zoom. zoom === 1 → unchanged.
      const offsetHeight = el.offsetHeight
      const zoom = offsetHeight ? el.getBoundingClientRect().height / offsetHeight : 1
      const desiredVisual = window.innerHeight - top - FILL_BOTTOM_MARGIN
      setHeight(Math.max(minHeight, Math.round(desiredVisual / (zoom || 1))))
    }
    recompute()

    // Re-measure every frame for the length of an MUI Collapse (300ms + a frame of
    // slack). React bails out when the height is unchanged, so once the layout settles
    // these frames cost nothing. In principle the ResizeObserver below covers this; in
    // practice it was measured NOT firing at all in this preview, so the animation
    // window cannot depend on it.
    let rafId = 0
    const started = performance.now()
    const track = () => {
      recompute()
      if (performance.now() - started < 320) rafId = requestAnimationFrame(track)
    }
    rafId = requestAnimationFrame(track)
    const firstRaf = rafId

    window.addEventListener('resize', recompute)

    // `resize` alone does NOT reliably fire on browser zoom, and pinch-zoom changes
    // only the visual viewport. Observe the document (page zoom + window resize) and
    // the visualViewport (pinch) as well.
    const ro = new ResizeObserver(recompute)
    ro.observe(document.documentElement)
    ro.observe(document.body)
    // Content ABOVE the box (skeleton→content swap, a collapsing banner, sticky
    // header/tabs settling inside a scroll container) reflows its top WITHOUT changing
    // the viewport, so none of the observers above fire and the one-time measure goes
    // stale. Observe the ancestor chain, whose auto height tracks that content. Can't
    // loop: React bails on an unchanged height, so a no-op recompute settles at once.
    let node = ref.current?.parentElement ?? null
    for (let i = 0; node && i < 4; i++) {
      ro.observe(node)
      node = node.parentElement
    }

    const vv = window.visualViewport
    vv?.addEventListener('resize', recompute)

    // CSS `zoom` changes fire NONE of the above: the <html> layout box is unchanged and
    // the visualViewport does not move. Without a trigger the stale height is
    // reinterpreted at the new zoom and the pane overflows the viewport. Poll the
    // measured factor and re-fill only when it actually changes.
    let lastZoom = -1
    const pollZoom = () => {
      const box = ref.current
      if (!box) return
      const z = box.offsetHeight ? box.getBoundingClientRect().height / box.offsetHeight : 1
      if (Math.abs(z - lastZoom) > 0.001) {
        lastZoom = z
        recompute()
      }
    }
    const zoomPoll = window.setInterval(pollZoom, 400)

    return () => {
      window.removeEventListener('resize', recompute)
      ro.disconnect()
      vv?.removeEventListener('resize', recompute)
      cancelAnimationFrame(firstRaf)
      cancelAnimationFrame(rafId)
      window.clearInterval(zoomPoll)
    }
    // `deps` is spread deliberately: callers pass the values that move their pane's
    // top (row counts, loading flags), and a stable-length array is the contract.
  }, [enabled, measureSelector, minHeight, ...deps])

  return { ref, height }
}
